import React, { useEffect, useState } from "react";
import Filter from "./CourseFilter";
import CourseCreateModal from "./CourseCreateModal";
import CourseImportModal from "./CourseImportModal";
import CourseAssignModal from "./CourseAssignModal";
import CourseDetailModal from "./CourseDetailModal";
import Spinner from "../../../components/ui/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faList, faBookOpen } from "@fortawesome/free-solid-svg-icons";
import {
  Clock,
  BookOpen,
  BarChart,
  ChevronRight,
  Sparkles,
  CircleHelp,
  FilePenLine,
  FileUp,
} from "lucide-react";
import {
  getAllCoursesNoPaging,
  deleteCourse,
  toggleCourseStatus,
} from "../../../api/elearning/courses/courses";

import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import { startCoursesPageTour } from "./coursesPageTour";
import { startCourseFormTour } from "./courseFormTour";
import { startCourseImportTour } from "./courseImportTour";

import { getLevelOptions } from "../../../utils/dropdownOptions";

const levelOptions = getLevelOptions();

const renderLevels = (levelString) => {
  if (!levelString) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {levelString.split(",").map((level) => {
        const key = level.trim();

        const option = levelOptions.find((o) => o.value === key);
        const label = option ? option.label : key;

        return (
          <span
            key={key}
            className="px-2 py-[2px] text-xs rounded bg-red-50 text-red-700"
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

// 🔹 GROUP BY SUBJECT
const groupBySubject = (courses = []) => {
  const map = {};
  courses.forEach((c) => {
    if (!map[c.subject_id]) {
      map[c.subject_id] = {
        subject_id: c.subject_id,
        subject_name: c.subject_name,
        subject_sort_order: c.subject_sort_order ?? 0,
        courses: [],
      };
    }
    map[c.subject_id].courses.push(c);
  });

  return Object.values(map).sort(
    (a, b) => a.subject_sort_order - b.subject_sort_order
  );
};

const CourseCardPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [queuedTour, setQueuedTour] = useState(null);
  const canQuickTour = hasPermission("elearning.courses", "R");

  const fetchCourses = async (filters = {}) => {
    try {
      setLoading(true);
      const data = await getAllCoursesNoPaging(filters);
      setCourses(data || []);
    } catch (err) {
      console.error("Lỗi khi lấy danh sách khóa học:", err);
      showErrorToast("Không thể tải danh sách khóa học");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(filters);
  }, [filters]);

  useEffect(() => {
    if (queuedTour === "form" && showForm) {
      window.setTimeout(() => {
        startCourseFormTour();
        setQueuedTour(null);
      }, 220);
    }
    if (queuedTour === "import" && showImportExcel) {
      window.setTimeout(() => {
        startCourseImportTour();
        setQueuedTour(null);
      }, 220);
    }
  }, [queuedTour, showForm, showImportExcel]);

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-courses-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  const handlePageTour = () => {
    window.setTimeout(() => startCoursesPageTour(), 80);
  };

  const handleFormTour = () => {
    setEditingCourse(null);
    setQueuedTour("form");
    setShowForm(true);
  };

  const handleImportTour = () => {
    setQueuedTour("import");
    setShowImportExcel(true);
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      await deleteCourse(courseId);
      showSuccessToast("Đã xóa khóa học");
      setShowDetailModal(false);
      fetchCourses(filters);
    } catch (err) {
      showErrorToast("Không thể xóa khóa học");
    }
  };

  const handleToggleCourseStatus = async (courseId, nextActive) => {
    try {
      await toggleCourseStatus(courseId, nextActive);
      showSuccessToast("Đã cập nhật trạng thái");
      setShowDetailModal(false);
      fetchCourses(filters);
    } catch (err) {
      showErrorToast("Không thể cập nhật trạng thái");
    }
  };

  const groupedSubjects = groupBySubject(courses);

  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="courses-header">
        <div className="flex items-center gap-4">
          {/* Icon Wrapper với Shadow Red */}
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faBookOpen}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            {/* Title với style font-black và uppercase đồng bộ */}
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">khóa học</span>
            </h1>

            {/* Subtitle với hiệu ứng pulse và font tracking-widest */}
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{courses.length}</span> khóa học
                đang hiển thị
              </p>
            </div>
          </div>
        </div>
        {canQuickTour ? (
          <div className="flex items-center gap-2" data-tour="courses-header-guides">
            <HeaderIconButton icon={Sparkles} label="Hướng dẫn nhanh" onClick={handlePageTour} />
            <HeaderIconButton icon={FilePenLine} label="Tour form" onClick={handleFormTour} />
            <HeaderIconButton icon={FileUp} label="Tour import" onClick={handleImportTour} />
            <HeaderIconButton icon={CircleHelp} label="Hướng dẫn sử dụng" onClick={openGuide} />
          </div>
        ) : null}
      </div>

      <div data-tour="courses-filter">
        <Filter
          onNewClick={() => setShowForm(true)}
          onImportExcelClick={() => setShowImportExcel(true)}
          onAssignClick={() => setShowAssignModal(true)}
          onFilterChange={setFilters}
        />
      </div>

      {loading ? (
        <Spinner />
      ) : groupedSubjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center text-gray-500 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
          <FontAwesomeIcon icon={faBookOpen} className="text-4xl text-gray-300 mb-4" />
          <p className="font-semibold text-gray-700">Chưa có khóa học phù hợp lọc</p>
          <p className="text-sm mt-1 max-w-md">
            Thử tìm bằng từ khóa khác hoặc xóa bộ lọc. Bạn cũng có thể thêm khóa học mới hoặc nhập từ Excel.
          </p>
        </div>
      ) : (
        groupedSubjects.map((group) => (
          <div key={group.subject_id} className="mt-8" data-tour="courses-subject-group">
            {/* SUBJECT HEADER */}
            <div className="flex items-center justify-between mb-6 group">
              <div className="flex items-center gap-3">
                {/* Thanh trang trí dọc */}
                <div className="w-1.5 h-6 bg-red-600 rounded-full" />
                <h2 className="text-xl font-black text-gray-800 tracking-tight">
                  {group.subject_name}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-bold border border-gray-200 shadow-sm">
                  {group.courses.length} khóa học
                </span>
              </div>

              {/* Đường kẻ ngang mảnh nối dài */}
              <div className="flex-1 ml-4 h-[1px] bg-gradient-to-r from-gray-200 to-transparent opacity-50" />
            </div>

            {/* COURSE GRID */}
            <div className="grid grid-cols-1 mb:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6" data-tour="courses-grid">
              {group.courses.map((course) => {
                const isActive = course.is_active;

                return (
                  <div
                    key={course.id}
                    onClick={() => {
                      setSelectedCourse(course.id);
                      setShowDetailModal(true);
                    }}
                    className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm 
                   hover:shadow-xl hover:-translate-y-1 transition-all duration-300 
                   cursor-pointer overflow-hidden flex flex-col"
                  >
                    {/* STATUS BADGE */}
                    <div className="absolute top-3 right-3 z-10">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium backdrop-blur-md
            ${isActive
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-600 border border-gray-200"
                          }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isActive
                              ? "bg-green-500 animate-pulse"
                              : "bg-gray-400"
                            }`}
                        />
                        {isActive ? "Hoạt động" : "Tạm ngưng"}
                      </span>
                    </div>

                    <div className="p-5 flex-1">
                      {/* COURSE NAME */}
                      <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-red-600 transition-colors line-clamp-2 pr-20">
                        {course.name}
                      </h3>

                      {/* DESCRIPTION */}
                      {course.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mb-4 leading-relaxed">
                          {course.description}
                        </p>
                      )}

                      {/* STATS GRID */}
                      <div className="space-y-3 pt-4 border-t border-gray-50">
                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 mr-3">
                            <BarChart size={16} />
                          </div>
                          <span className="text-gray-500 w-20">Trình độ:</span>
                          <span className="font-medium text-gray-700">
                            {renderLevels(course.level)}
                          </span>
                        </div>

                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 mr-3">
                            <Clock size={16} />
                          </div>
                          <span className="text-gray-500 w-20">
                            Thời lượng:
                          </span>
                          <span className="font-medium text-gray-700">
                            {course.duration_hours} giờ
                          </span>
                        </div>

                        <div className="flex items-center text-sm">
                          <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 mr-3">
                            <BookOpen size={16} />
                          </div>
                          <span className="text-gray-500 w-20">Bài học:</span>
                          <span className="font-medium text-gray-700">
                            {course.lesson_count || 0} bài
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* BOTTOM ACTION (Visual only) */}
                    <div className="px-5 py-3 bg-gray-50 flex justify-between items-center group-hover:bg-red-50 transition-colors">
                      <span className="text-xs font-semibold text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        Xem chi tiết
                      </span>
                      <ChevronRight
                        size={14}
                        className="text-gray-400 group-hover:text-red-600 transform group-hover:translate-x-1 transition-all"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <CourseCreateModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingCourse(null);
          setQueuedTour(null);
        }}
        editingCourse={editingCourse}
        onSaved={(opts) => {
          fetchCourses(filters);
          if (!opts?.keepOpen) {
            setShowForm(false);
            setEditingCourse(null);
          }
        }}
      />

      <CourseImportModal
        isOpen={showImportExcel}
        onClose={() => {
          setShowImportExcel(false);
          setQueuedTour(null);
        }}
        onSuccess={() => fetchCourses(filters)}
      />

      <CourseAssignModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSaved={() => fetchCourses(filters)}
      />

      {showDetailModal && selectedCourse && (
        <CourseDetailModal
          courseId={selectedCourse}
          onClose={() => setShowDetailModal(false)}
          onDelete={handleDeleteCourse}
          onToggleStatus={handleToggleCourseStatus}
          onEdit={(course) => {
            setEditingCourse(course);
            setShowDetailModal(false);
            setShowForm(true);
          }}
        />
      )}
    </div>
  );
};

export default CourseCardPage;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
        {label}
      </span>
    </button>
  );
}
